import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveRetiredOrganizationSlug } from "@/lib/publicOrganizationRpc";

/**
 * When an organization page's own lookup found nothing, ask whether the slug
 * is a RETIRED one and, if so, replace the URL with the current slug (keeping
 * `suffix`, the query and the hash). Returns true while that question is open
 * or the redirect is about to happen, so the page shows its loading state
 * instead of flashing not-found.
 *
 * Call it unconditionally, above the page's early returns.
 */
export function useRetiredSlugRedirect(slug: string, missing: boolean, suffix = ""): boolean {
  const navigate = useNavigate();
  const location = useLocation();
  const enabled = missing && !!slug;
  const { data, isLoading } = useQuery({
    queryKey: ["retired-org-slug", slug],
    queryFn: () => resolveRetiredOrganizationSlug(slug),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (!enabled || !data || data === slug) return;
    navigate(`/organization/${encodeURIComponent(data)}${suffix}${location.search}${location.hash}`, { replace: true });
  }, [enabled, data, slug, suffix, navigate, location.search, location.hash]);

  return enabled && (isLoading || (!!data && data !== slug));
}
